import Symbols from "./Symbols.mts";
import UFunction from "./UFunction.mts";
import UObject from "./UObject.mts";

namespace UProxy {
  export const get = <Key extends UObject.Key, R>(
    keys: readonly Key[],
    receive: (key: Key) => R
  ) =>
    new Proxy({} as Record<Key, R>, {
      get(target, p) {
        if (keys.includes(p as Key))
          return (target[p as Key] ??= receive(p as Key));
      },
    });

  // Include `[UProxy.merge.excludeKeys]: true` in target or source to include
  // it's keys from spreading. This is useful when lazy loading data should not
  // be evaluated when spreading, such as associations.
  declare const excludeKeys: unique symbol;
  merge.excludeKeys = Symbol("excludeKeys") as typeof excludeKeys;
  declare const original: unique symbol;
  merge.original = Symbol("original") as typeof original;
  namespace merge {
    export type Result<
      Target extends UObject.Any | undefined,
      Source extends UObject.Any | undefined,
    > = (undefined extends Extract<Target, undefined>
      ? Partial<Target>
      : Target) &
      (undefined extends Extract<Source, undefined> ? Partial<Source> : Source);
  }
  export function merge<
    Target extends UObject.Any | undefined,
    Source extends UObject.Any | undefined,
  >(target: Target, source: Source): merge.Result<Target, Source> {
    return (function next(target, source, parent?: { ref: any; key: any }) {
      if (!target) return source as any;
      if (!source) return target as any;

      const isFn = typeof target === "function" || typeof source === "function";
      const isArray = Array.isArray(target) || Array.isArray(source);

      return new Proxy(
        Object.assign(
          (() => {
            // Array.isArray bypasses proxy traps, must use actual array as target when applicable
            if (isArray) return [];
            // apply proxy trap only applies when target is a function
            if (isFn) return () => {};
            return {};
          })(),
          { [merge.original]: { target, source } }
        ),
        {
          get(_, p) {
            if (p === merge.excludeKeys) return;
            if (p === Symbols.typeOf)
              // @ts-expect-error
              return source[p] || target[p];

            const targetField = (target as any)[p];
            const sourceField = (source as any)[p];

            if (
              targetField &&
              p in source &&
              (UObject.is(targetField) || UFunction.is(targetField))
            )
              return next(targetField, sourceField, {
                ref: UFunction.is(sourceField) ? source : target,
                key: p,
              });

            return p in source ? sourceField : targetField;
          },
          set(_, p, value) {
            if (Object.getOwnPropertyDescriptor(source, p)?.set)
              return Reflect.set(source, p, value);
            return Reflect.set(target, p, value);
          },
          apply(_, __, args) {
            if (UFunction.is(source))
              return parent ? parent.ref[parent.key](...args) : source(...args);
            return parent
              ? parent.ref[parent.key](...args)
              : (target as any)(...args);
          },
          construct(_, args) {
            try {
              return new (target as any)(...args);
            } catch (e) {
              return new (source as any)(...args);
            }
          },
          has(_, p) {
            return p in source || p in target;
          },
          ownKeys() {
            const excludeKeys = (subject: UObject.Any) =>
              merge.excludeKeys in subject && subject[merge.excludeKeys];

            // TODO: Use UArray.dedupe after namespace transformer, will allow for
            // circular dependencies between namespaces
            return [
              ...new Set([
                ...(excludeKeys(target) ? [] : Reflect.ownKeys(target)),
                ...(excludeKeys(source) ? [] : Reflect.ownKeys(source)),
              ]),
            ];
          },
          getOwnPropertyDescriptor(_, p) {
            // TODO: Getter from one and setter from the other can be merged
            return Reflect.getOwnPropertyDescriptor(
              p in source ? source : target,
              p
            );
          },
          getPrototypeOf() {
            // Choose target only if it's an instance of a subclass of source. We
            // want to favor the more specific constructor
            if (
              UObject.is(source) &&
              UObject.is(target) &&
              (!source.constructor || target instanceof source.constructor)
            )
              return Object.getPrototypeOf(target);

            return Object.getPrototypeOf(source);
          },
        }
      ) as any;
    })(target, source);
  }

  export type PathProxy<Args extends any[], Result> = {
    [K in UObject.Key]: ((...args: Args) => Result) & PathProxy<Args, Result>;
  };

  export const createPathProxy = <Args extends any[], Result>(
    handler: (path: UObject.Key[]) => (...args: Args) => Result
  ): PathProxy<Args, Result> => {
    return (function next(path: UObject.Key[] = []) {
      return new Proxy(UFunction.noop, {
        get(_, p) {
          return next([...path, p]);
        },
        apply(_, __, args) {
          // @ts-expect-error
          return handler(path)(...args);
        },
      });
    })() as any;
  };

  /**
   * @deprecated Use `deepDecorate` instead.
   */
  export const deepShim = deepDecorate;
  export function deepDecorate<Obj extends UObject.Any>(
    obj: Obj,
    shim: (original: UFunction.Any, path: UObject.Key[]) => UFunction.Any
  ): Obj {
    return (function next(
      current = obj,
      // Track path for better error messages
      path: UObject.Key[] = []
    ) {
      return new Proxy(
        // Allows native error to be shown if current is not a function
        UFunction.is(current) ? () => {} : {},
        {
          get(_, p): any {
            const shimField = (shim(current as UFunction.Any, path) as any)[p];
            if (shimField) return shimField;

            const field = (current as any)[p];
            return field && next((current as any)[p], [...path, p]);
          },
          apply(_, __, args) {
            if (!UFunction.is(current))
              throw new Error(`<object>.${path.join(".")} is not a function`);

            const res = shim(current as UFunction.Any, path);
            if (!res)
              throw new Error(
                "UProxy.deepDecorate: `shim` (2nd argument) must return a function."
              );
            return res(...args);
          },
          has(_, p) {
            return p in current;
          },
          ownKeys() {
            return Reflect.ownKeys(current);
          },
          getOwnPropertyDescriptor(_, p) {
            return Reflect.getOwnPropertyDescriptor(current, p);
          },
        }
      ) as any;
    })();
  }

  declare const access: unique symbol;
  deepDecorate.lazy = (() => {
    class UndefinedPathError extends TypeError {
      constructor(path: UObject.Key[]) {
        super(
          `Cannot read property \`${String(path.at(-1))}\` of undefined at path \`${path.slice(0, -1).join(".")}\``
        );
      }
    }

    lazy.access = Symbol("access") as typeof access;
    return lazy;
    function lazy<Obj extends UObject.Any>(
      access: () => Obj,
      shim: (original: UFunction.Any, path: UObject.Key[]) => UFunction.Any
    ): Obj {
      return (function next(
        current = access,
        path: UObject.Key[] = [],
        _this?: () => any
      ) {
        Object.defineProperty(current, lazy.access, { get: current });
        return new Proxy(current, {
          get(_, p) {
            if (p === Symbols.typeOf) return typeof current();
            return next(
              () => {
                const parent = current();
                if (parent == null) throw new UndefinedPathError(path);
                return (parent as any)[p];
              },
              [...path, p],
              current
            );
          },
          apply(_, __, args) {
            const fn = current();
            if (!UFunction.is(fn))
              throw new TypeError(
                `Attempted to call non-function at path \`${path.join(".")}\``
              );

            const res = shim((fn as UFunction.Any).bind(_this?.()), path);
            if (!res)
              throw new TypeError(
                "UProxy.deepDecorate.lazy: `shim` (2nd argument) must return a function."
              );
            return res(...args);
          },
          has(_, p) {
            if (!current()) return false;
            return p in current();
          },
          ownKeys() {
            return Reflect.ownKeys(current());
          },
          getOwnPropertyDescriptor(_, p) {
            return Reflect.getOwnPropertyDescriptor(current(), p);
          },
          getPrototypeOf() {
            try {
              return Object.getPrototypeOf(current());
            } catch (e) {
              // Path is invalid or current is nullish
              return null;
            }
          },
        });
      })() as unknown as Obj;
    }
  })();

  export function pick<Obj extends UObject.Any, Properties extends keyof Obj>(
    target: Obj,
    properties: Properties[]
  ): Pick<Obj, Properties> {
    return new Proxy(target, {
      get(_, p: any) {
        if (!properties.includes(p))
          throw new Error(`Property ${p} inaccessible.`);
        return (target as any)[p];
      },
    });
  }
}

export default UProxy;
